import { formatInches } from "@betterpos/domain";
import type { ItemDetail } from "../../data/catalog.ts";
import { capitalize } from "../utils.ts";
import { AttrRow, type AttrRowSpec } from "./AttrRow.tsx";

type FirearmAttrs = NonNullable<ItemDetail["firearmAttributes"]>;

// Re-export the shared AttrRow + spec type under the firearm-specific names
// used by ItemDetail (preserves the existing import path).
export { AttrRow };
export type FirearmRow = AttrRowSpec;

export function FirearmSpecLine({ a }: { a: FirearmAttrs }) {
  // One-line glance summary for the retail floor:
  // caliber · barrel. Keeps staff from drilling into the Firearm attributes
  // card for the two most-asked things. Condition rides as a colored pill
  // alongside price so it's immediately scannable on the floor.
  const parts: string[] = [a.caliberGauge];
  const barrel = formatInches(a.barrelLengthIn);
  if (barrel) parts.push(`${barrel} barrel`);
  return <p className="text-[15px] font-medium text-slate-600 tabular-nums">{parts.join(" · ")}</p>;
}

export function buildFirearmRows(a: FirearmAttrs): AttrRowSpec[] {
  return [
    { label: "Manufacturer", value: a.manufacturer, required: true },
    { label: "Importer", value: a.importer },
    { label: "Model", value: a.model, required: true },
    { label: "ATF type", value: capitalize(a.atfType), required: true },
    { label: "Action type", value: a.actionType ? capitalize(a.actionType) : null },
    { label: "Caliber / gauge", value: a.caliberGauge, mono: true, required: true },
    { label: "Barrel", value: formatInches(a.barrelLengthIn), mono: true },
    { label: "Overall", value: formatInches(a.overallLengthIn), mono: true },
    {
      label: "Magazine capacity",
      value: a.magazineCapacity != null ? `${a.magazineCapacity} rd` : null,
      mono: true,
    },
    { label: "Included magazines", value: a.includedMagazines },
    { label: "Finish", value: a.finish },
    { label: "Handedness", value: a.handedness ? capitalize(a.handedness) : null },
    { label: "Country of origin", value: a.countryOfOrigin },
    { label: "Condition", value: capitalize(a.conditionNewUsed), required: true },
    { label: "NFA type", value: a.nfaType ? capitalize(a.nfaType) : null },
    { label: "Antique", value: a.isAntique ? "Yes" : "No" },
    { label: "PMF", value: a.isPrivatelyMade ? "Yes" : "No" },
  ];
}
